import React from 'react';

import {
  FieldValues,
  Path,
  PathValue,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import {
  TextField,
  Autocomplete,
  AutocompleteProps,
  TextFieldProps,
  InputBaseProps,
} from '@mui/material';

type LbAutocompleteProps<T extends FieldValues, TName extends Path<T>, TOption> = {
  InputProps?: InputBaseProps;
} & UseControllerProps<T, TName> &
  Pick<
    AutocompleteProps<TOption, false, true, false>,
    'options' | 'getOptionLabel' | 'isOptionEqualToValue' | 'getOptionKey'
  > &
  Pick<TextFieldProps, 'label' | 'variant' | 'size' | 'margin'>;

const LbAutocomplete = <T extends FieldValues, TName extends Path<T>, TOption>({
  control,
  name,
  rules,
  defaultValue = null as PathValue<T, TName>,
  options,
  label,
  getOptionKey,
  variant,
  size,
  margin,
  shouldUnregister = false,
  getOptionLabel,
  isOptionEqualToValue,
  InputProps = {},
}: LbAutocompleteProps<T, TName, TOption>) => {
  const {
    field: { ref, onChange, ...field },
    fieldState: { error },
  } = useController<any>({
    control,
    name,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const isRequired = !!rules?.required;

  return (
    <Autocomplete
      {...field}
      fullWidth
      onChange={(_, data) => onChange(data)}
      size={size}
      disableClearable
      getOptionKey={getOptionKey}
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      slotProps={{
        popper: { className: 'general-style variant-select', placement: 'bottom-start' },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          helperText={error?.message}
          error={!!error}
          label={label}
          required={isRequired}
          variant={variant}
          size={size}
          margin={margin}
          inputRef={ref}
          slotProps={{
            input: { ...params.InputProps, ...InputProps },
          }}
        />
      )}
    />
  );
};

export { LbAutocomplete };
